import React, { useState } from 'react';
import {
    getEventRateSubtitleComponents,
    getFunnelSubtitleComponents,
    getPropOTInfoText,
    getTRIOpDateSubtitleComponents,
    getTRIOpSubtitleComponents,
} from '@components-clean/core/charts/scatter-plot/utils';
import { DropDownList } from '@components-clean/core/drop-down-list';
import PanelFrame from '@components-clean/core/panel-frame';
import { LineChartsPage } from '@pages-clean/dummy-page/pages/line-charts-page';
import {
    ControlHubModelPeriodType,
    EventRatesOTBenchmarkType,
    EventRatesOTCountryBenchmarkType,
    EventRatesOTThresholdType,
    ModelPlotBy,
    ModelPlotCalculation,
    PanelBenchmark,
    PanelIntervalType,
    PanelModelType,
    PanelSiteScoring,
    PanelTheme,
    PanelThresholdType,
    PanelType,
    ProportionsOTBenchmarkType,
    ProportionsOTThresholdType,
    ThresholdCalculation,
} from 'types/graphql-global-types';
import { components } from 'types/rest-schema';
import {
    columnChartData,
    comboColumnLineData,
    donutData,
    eventRatesDataCountry,
    eventRatesDataStudy,
    funnelData,
    funnelDataAprox,
    heatMapData,
    proportionsOTStudyData,
    radialChartExample1Data,
    radialChartExample2Data,
    radialChartExample3Data,
    rateNegativeBinomial,
    ratePoisson,
    ratePoissonHigherPurple,
    ratePoissonLowerPurple,
    sankeyChartExample1Data,
    sankeyChartExample2Data,
    stackedBarChartExampleCountryData,
    stackedBarChartExampleBellCurveData,
    stackedBarChartExampleSiteData,
    stackedBarChartExampleSubjectData,
    stackedColumnChartExampleCountryData,
    stackedColumnChartExampleBellCurveData,
    stackedColumnChartExampleSiteData,
    stackedColumnChartExampleSubjectData,
    triOpData,
    triOpDateData,
} from 'views-clean/pages/dummy-page/pages/charts-data';

type ThemeText = {
    theme: PanelTheme;
    text: string;
};
const themeData: ThemeText[] = [
    { theme: PanelTheme.THEME_1, text: 'Theme 1' },
    { theme: PanelTheme.THEME_2, text: 'Theme 2' },
    { theme: PanelTheme.THEME_3, text: 'Theme 3' },
    { theme: PanelTheme.THEME_4, text: 'Theme 4' },
];

const Charts = () => {
    const [theme, setTheme] = useState<ThemeText | null>(themeData[3]);
    return (
        <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
            <DropDownList
                data-testid="panel-theme-picker"
                style={{ width: '200px' }}
                label="Theme"
                data={themeData}
                textField="text"
                onChange={(e) => setTheme(e.value as ThemeText)}
                clearButton={false}
                value={theme}
            />
            <h2>Combo stack column</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title="Combo stacked column/line graph"
                    data-testid="panel-combo-graph"
                    panelType={PanelType.GRAPH}
                    graphType="COMBOCOLUMNLINE"
                    panelData={comboColumnLineData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                />
            </div>
            <h2>Donut</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title="Donut graph"
                    data-testid="panel-donut-graph"
                    panelType={PanelType.GRAPH}
                    graphType="DONUT"
                    panelData={donutData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                />
            </div>
            <h2>Heatmap</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title="Heatmap graph"
                    data-testid="panel-heatmap-graph"
                    panelType={PanelType.GRAPH}
                    graphType="HEATMAP"
                    panelData={heatMapData}
                    showWaterMark
                    hidePanelSetup
                    xAxisTitle="Action Type"
                    yAxisTitle="Site #"
                />
            </div>
            <h2>Column</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title={columnChartData.plotTitle}
                    data-testid="panel-column-graph"
                    panelType={PanelType.GRAPH}
                    graphType="COLUMN"
                    panelData={columnChartData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    siteId={123}
                />
            </div>
            <h2>Bubble</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title="Bubble Chart Example"
                    data-testid="panel-bubble-graph"
                    panelType={PanelType.GRAPH}
                    graphType="BUBBLE"
                    panelData={[
                        {
                            name: 'Risks',
                            data: [
                                { x: 1, y: 5, z: 10, color: '#4caf50', name: 'Low' },
                                { x: 2, y: 3, z: 20, color: '#ff9800', name: 'Medium' },
                                { x: 3, y: 7, z: 30, color: '#f44336', name: 'High' },
                                { x: 4, y: 2, z: 15, color: '#9e9e9e', name: 'Inactive' },
                                { x: 2, y: 6, z: 25, color: '#2196f3', name: 'Moderate' },
                                { x: 2, y: 8, z: 18, color: '#00bcd4', name: 'Elevated' },
                                { x: 3, y: 3, z: 12, color: '#ff5722', name: 'Critical' },
                                { x: 5, y: 3, z: 22, color: '#673ab7', name: 'Severe' },
                                { x: 5, y: 5, z: 28, color: '#e91e63', name: 'Active' },
                                { x: 1, y: 8, z: 16, color: '#ffc107', name: 'Warning' },
                            ],
                        },
                    ]}
                    hidePanelSetup
                    xAxisTitle="Impact"
                    yAxisTitle="Probability"
                />
            </div>
            <h2>Sankey</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title={sankeyChartExample1Data.title}
                    data-testid="sankey-graph"
                    panelType={PanelType.GRAPH}
                    graphType="SANKEY"
                    panelData={sankeyChartExample1Data}
                    showLegend
                    hidePanelSetup
                />
                <PanelFrame
                    version={1}
                    title={sankeyChartExample2Data.title}
                    data-testid="sankey-graph"
                    panelType={PanelType.GRAPH}
                    graphType="SANKEY"
                    panelData={sankeyChartExample2Data}
                    showLegend
                    hidePanelSetup
                />
            </div>
            <h2>Radial</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title={radialChartExample1Data.plotTitle}
                    data-testid="radial-graph"
                    panelType={PanelType.GRAPH}
                    graphType="RADIAL"
                    panelData={radialChartExample1Data}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                />
                <PanelFrame
                    version={1}
                    title={radialChartExample2Data.plotTitle}
                    data-testid="radial-graph"
                    panelType={PanelType.GRAPH}
                    graphType="RADIAL"
                    panelData={radialChartExample2Data}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                />
                <PanelFrame
                    version={1}
                    title={radialChartExample3Data.plotTitle}
                    data-testid="radial-graph"
                    panelType={PanelType.GRAPH}
                    graphType="RADIAL"
                    panelData={radialChartExample3Data}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                />
            </div>
            <h2>Stacked bar</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title={stackedBarChartExampleCountryData.plotTitle}
                    data-testid="stacked-bar-graph"
                    panelType={PanelType.GRAPH}
                    graphType="STACKEDBAR"
                    panelData={stackedBarChartExampleCountryData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                />
                <PanelFrame
                    version={1}
                    title={stackedBarChartExampleBellCurveData.plotTitle}
                    data-testid="stacked-bar-graph"
                    panelType={PanelType.GRAPH}
                    graphType="STACKEDBAR"
                    panelData={stackedBarChartExampleBellCurveData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                />
                <PanelFrame
                    version={1}
                    title={stackedBarChartExampleSiteData.plotTitle}
                    data-testid="stacked-bar-graph"
                    panelType={PanelType.GRAPH}
                    graphType="STACKEDBAR"
                    panelData={stackedBarChartExampleSiteData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                />
                <PanelFrame
                    version={1}
                    title={stackedBarChartExampleSubjectData.plotTitle}
                    data-testid="stacked-column-graph"
                    panelType={PanelType.GRAPH}
                    graphType="STACKEDBAR"
                    panelData={stackedBarChartExampleSubjectData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                />
            </div>
            <h2>Stacked column</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title={stackedColumnChartExampleCountryData.plotTitle}
                    data-testid="stacked-column-graph"
                    panelType={PanelType.GRAPH}
                    graphType="STACKEDCOLUMN"
                    panelData={stackedColumnChartExampleCountryData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                />
                <PanelFrame
                    version={1}
                    title={stackedColumnChartExampleBellCurveData.plotTitle}
                    data-testid="stacked-column-graph"
                    panelType={PanelType.GRAPH}
                    graphType="STACKEDCOLUMN"
                    panelData={stackedColumnChartExampleBellCurveData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                />
                <PanelFrame
                    version={1}
                    title={stackedColumnChartExampleSiteData.plotTitle}
                    data-testid="stacked-column-graph"
                    panelType={PanelType.GRAPH}
                    graphType="STACKEDCOLUMN"
                    panelData={stackedColumnChartExampleSiteData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                />
                <PanelFrame
                    version={1}
                    title={stackedColumnChartExampleSubjectData.plotTitle}
                    data-testid="stacked-column-graph"
                    panelType={PanelType.GRAPH}
                    graphType="STACKEDCOLUMN"
                    panelData={stackedColumnChartExampleSubjectData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                />
            </div>
            <LineChartsPage theme={theme?.theme} />
            <h2>TRI Operational</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title="TRI Operational"
                    infoText={getTRIOpSubtitleComponents(
                        { value: triOpData.threshold1 || 1, type: PanelThresholdType.SAMPLE_MEDIAN },
                        { value: triOpData.threshold2 || 1.3706441393875386, type: PanelThresholdType.SAMPLE_MEDIAN },
                        true,
                    )}
                    data-testid="tri-op-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.TRI_OPERATIONAL,
                        threshold1Type: PanelThresholdType.SAMPLE_MEDIAN,
                        threshold2Type: PanelThresholdType.SAMPLE_MEAN,
                        stableValue: 5,
                        stableVariableType: 'Form',
                    }}
                    panelData={triOpData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
                <PanelFrame
                    version={1}
                    title="Unfiltered - TRI Operational"
                    data-testid="tri-op-panel"
                    infoText={getTRIOpSubtitleComponents(
                        { value: triOpData.threshold1 || 1, type: PanelThresholdType.SAMPLE_MEDIAN },
                        { value: triOpData.threshold2 || 1.3706441393875386, type: PanelThresholdType.SAMPLE_MEDIAN },
                        true,
                    )}
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.TRI_OPERATIONAL,
                        threshold1Type: PanelThresholdType.SAMPLE_MEDIAN,
                        threshold2Type: PanelThresholdType.SAMPLE_MEAN,
                        stableValue: 5,
                        stableVariableType: 'Form',
                    }}
                    panelData={
                        {
                            ...triOpData,
                            points: triOpData?.points?.map(({ isHighlighted, ...rest }) => ({
                                ...rest,
                            })),
                        } as components['schemas']['TRIOpResultDto']
                    }
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
            </div>
            <h2>Rate Group</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title="Poisson"
                    data-testid="rate-poisson-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.RATE_GROUP,
                    }}
                    panelData={ratePoisson}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
                <PanelFrame
                    version={1}
                    title="Poisson - Higher Purple"
                    data-testid="rate-poisson-higher-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.RATE_GROUP,
                        siteScoring: PanelSiteScoring.HIGHER_PURPLE,
                    }}
                    panelData={ratePoissonHigherPurple}
                    showLegend
                    showWaterMark
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
                <PanelFrame
                    version={1}
                    title="Poisson - Lower Purple"
                    data-testid="rate-poisson-lower-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.RATE_GROUP,
                        siteScoring: PanelSiteScoring.LOWER_PURPLE,
                    }}
                    panelData={ratePoissonLowerPurple}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
                <PanelFrame
                    version={1}
                    title="Negative Binomial"
                    data-testid="rate-nb-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.RATE_GROUP,
                    }}
                    panelData={rateNegativeBinomial}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
            </div>
            <h2>Funnel</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={1}
                    title="Funnel - Approximate"
                    data-testid="rate-poisson-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.FUNNEL,
                        confidenceIntervalType: PanelIntervalType.APPROXIMATE,
                        benchmarkType: PanelBenchmark.STUDY_MEAN,
                        lowerAmber: 95.5,
                        upperAmber: 95.5,
                        lowerRed: 98.9,
                        upperRed: 98.9,
                    }}
                    infoText={getFunnelSubtitleComponents(
                        PanelBenchmark.STUDY_MEAN,
                        funnelData.benchmark,
                        false,
                        false,
                        95.5,
                        95.5,
                        98.9,
                        98.9,
                    )}
                    panelData={funnelDataAprox}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
                <PanelFrame
                    version={1}
                    title="Funnel - Exact"
                    data-testid="rate-poisson-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.FUNNEL,
                        confidenceIntervalType: PanelIntervalType.EXACT,
                        benchmarkType: PanelBenchmark.STUDY_MEAN,
                        lowerAmber: 60,
                        upperAmber: 60,
                        lowerRed: 70,
                        upperRed: 70,
                    }}
                    infoText={getFunnelSubtitleComponents(
                        PanelBenchmark.STUDY_MEAN,
                        funnelData.benchmark,
                        false,
                        false,
                        60,
                        60,
                        70,
                        70,
                    )}
                    panelData={funnelData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
                <PanelFrame
                    version={1}
                    title="Funnel - Exact - Study Median"
                    data-testid="rate-poisson-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.FUNNEL,
                        confidenceIntervalType: PanelIntervalType.EXACT,
                        benchmarkType: PanelBenchmark.STUDY_MEDIAN,
                        lowerAmber: 60,
                        upperAmber: 60,
                        lowerRed: 70,
                        upperRed: 70,
                    }}
                    infoText={getFunnelSubtitleComponents(
                        PanelBenchmark.STUDY_MEAN,
                        funnelData.benchmark,
                        false,
                        false,
                        60,
                        60,
                        70,
                        70,
                    )}
                    panelData={funnelData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
                <PanelFrame
                    version={1}
                    title="Funnel - Exact - Study Specfic"
                    data-testid="rate-poisson-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.FUNNEL,
                        confidenceIntervalType: PanelIntervalType.EXACT,
                        benchmarkType: PanelBenchmark.STUDY_SPECIFIC,
                        lowerAmber: 60,
                        upperAmber: 60,
                        lowerRed: 70,
                        upperRed: 70,
                    }}
                    infoText={getFunnelSubtitleComponents(
                        PanelBenchmark.STUDY_SPECIFIC,
                        funnelData.benchmark,
                        false,
                        false,
                        60,
                        60,
                        70,
                        70,
                    )}
                    panelData={funnelData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
                <PanelFrame
                    version={1}
                    title="Funnel - Exact - Hide Upper"
                    data-testid="rate-poisson-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.FUNNEL,
                        confidenceIntervalType: PanelIntervalType.EXACT,
                        benchmarkType: PanelBenchmark.STUDY_SPECIFIC,
                        hideUpper: true,
                        lowerAmber: 60,
                        upperAmber: 60,
                        lowerRed: 70,
                        upperRed: 70,
                    }}
                    infoText={getFunnelSubtitleComponents(
                        PanelBenchmark.STUDY_MEAN,
                        funnelData.benchmark,
                        false,
                        true,
                        60,
                        60,
                        70,
                        70,
                    )}
                    panelData={funnelData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
                <PanelFrame
                    version={1}
                    title="Funnel - Exact - Hide Lower"
                    data-testid="rate-poisson-panel"
                    panelType={PanelType.MODEL}
                    modelConfig={{
                        type: PanelModelType.FUNNEL,
                        confidenceIntervalType: PanelIntervalType.EXACT,
                        benchmarkType: PanelBenchmark.STUDY_SPECIFIC,
                        hideLower: true,
                        lowerAmber: 60,
                        upperAmber: 60,
                        lowerRed: 70,
                        upperRed: 70,
                    }}
                    infoText={getFunnelSubtitleComponents(
                        PanelBenchmark.STUDY_MEAN,
                        funnelData.benchmark,
                        true,
                        false,
                        60,
                        60,
                        70,
                        70,
                    )}
                    panelData={funnelData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
            </div>

            <h2>TRI Op Date</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={2}
                    title="Tri Op Date - Mean+SD"
                    data-testid="tri-op-date"
                    panelType={PanelType.TRI_OP_DATE}
                    modelConfig={{
                        stableValue: 5,
                        stableVariableType: 'Number',
                        thresholdCalculation: ThresholdCalculation.MEAN_SD,
                    }}
                    infoText={getTRIOpDateSubtitleComponents(
                        triOpDateData.amberThresholdValue,
                        triOpDateData.redThresholdValue,
                        ThresholdCalculation.MEAN_SD,
                        triOpDateData.modelRate,
                    )}
                    panelData={triOpDateData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
            </div>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={2}
                    title="Tri Op Date - Poisson"
                    data-testid="tri-op-date"
                    panelType={PanelType.TRI_OP_DATE}
                    modelConfig={{
                        stableValue: 5,
                        stableVariableType: 'Percentage',
                        thresholdCalculation: ThresholdCalculation.POISSON,
                    }}
                    infoText={getTRIOpDateSubtitleComponents(
                        triOpDateData.amberThresholdValue,
                        triOpDateData.redThresholdValue,
                        ThresholdCalculation.POISSON,
                        triOpDateData.modelRate,
                    )}
                    panelData={triOpDateData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
            </div>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    version={2}
                    title="Tri Op Date - Study Specific"
                    data-testid="tri-op-date"
                    panelType={PanelType.TRI_OP_DATE}
                    modelConfig={{
                        stableValue: 5,
                        stableVariableType: 'Number',
                        thresholdCalculation: ThresholdCalculation.STUDY_SPECIFIC,
                    }}
                    infoText={getTRIOpDateSubtitleComponents(
                        triOpDateData.amberThresholdValue,
                        triOpDateData.redThresholdValue,
                        ThresholdCalculation.STUDY_SPECIFIC,
                        triOpDateData.modelRate,
                    )}
                    panelData={triOpDateData}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Value"
                    yAxisTitle="Y Axis Value"
                />
            </div>

            <h2>Event Rate Chart</h2>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    title="Event Rates - Study Rate - Cumulative Months - Fixed Thresholds"
                    data-testid="event-rate"
                    panelData={eventRatesDataStudy}
                    panelType={PanelType.EVENT_RATES_OVER_TIME}
                    infoText={getEventRateSubtitleComponents(
                        EventRatesOTBenchmarkType.STUDY_RATE,
                        2000,
                        EventRatesOTThresholdType.FIXED,
                        ModelPlotCalculation.CUMULATIVE,
                        ModelPlotBy.MONTHS,
                        3,
                        2,
                        5,
                        4,
                        10,
                        { unit: 4, period: ControlHubModelPeriodType.MONTHS },
                    )}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                    version={1}
                    modelConfig={{
                        eventRateBenchmarkType: EventRatesOTBenchmarkType.STUDY_RATE,
                        eventRateThresholdType: EventRatesOTThresholdType.FIXED,
                        lowerAmber: 2,
                        upperAmber: 3,
                        lowerRed: 4,
                        upperRed: 5,
                        plotCalculation: ModelPlotCalculation.CUMULATIVE,
                        minSubjects: 10,

                        plotBy: ModelPlotBy.MONTHS,
                        numeratorName: 'Numerator',
                        denominatorName: 'Denominator',
                    }}
                />
            </div>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    title="Event Rates - Country Rate - Cumulative Months - Fixed Thresholds"
                    data-testid="event-rate"
                    panelData={eventRatesDataCountry}
                    panelType={PanelType.EVENT_RATES_OVER_TIME}
                    infoText={getEventRateSubtitleComponents(
                        EventRatesOTCountryBenchmarkType.COUNTRY_RATE,
                        2000,
                        EventRatesOTThresholdType.U_CHART,
                        ModelPlotCalculation.BY_TIME_PERIOD,
                        ModelPlotBy.WEEKS,
                        3,
                        2,
                        5,
                        4,
                        10,
                        { unit: 2, period: ControlHubModelPeriodType.YEARS },
                    )}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                    version={1}
                    modelConfig={{
                        eventRateBenchmarkType: EventRatesOTCountryBenchmarkType.COUNTRY_RATE,
                        eventRateThresholdType: EventRatesOTThresholdType.U_CHART,
                        lowerAmber: 2,
                        upperAmber: 3,
                        lowerRed: 4,
                        upperRed: 5,
                        plotCalculation: ModelPlotCalculation.BY_TIME_PERIOD,
                        minSubjects: 10,
                        plotBy: ModelPlotBy.WEEKS,
                        numeratorName: 'Country Numerator',
                        denominatorName: 'Country Denominator',
                    }}
                    popoutInfo={{
                        dataCutDate: '2025-27-11',
                        dataviewRef: '000000000000000000000000',
                    }}
                />
            </div>
            <div className="charts-section k-d-grid k-gap-2">
                <PanelFrame
                    title="Proportions Over Time - Study Level"
                    data-testid="proportions-ot"
                    panelData={proportionsOTStudyData}
                    panelType={PanelType.PROPORTIONS_OVER_TIME}
                    infoText={getPropOTInfoText(
                        ProportionsOTBenchmarkType.STUDY_RATE,
                        0.49,
                        ProportionsOTThresholdType.P_CHART,
                        ModelPlotCalculation.BY_TIME_PERIOD,
                        ModelPlotBy.WEEKS,
                        3,
                        2,
                        5,
                        4,
                        10,
                    )}
                    showLegend
                    hidePanelSetup
                    theme={theme?.theme}
                    xAxisTitle="X Axis Title"
                    yAxisTitle="Y Axis Title"
                    version={1}
                    modelConfig={{
                        proportionsOTBenchmarkType: ProportionsOTBenchmarkType.STUDY_RATE,
                        proportionsOTThresholdType: ProportionsOTThresholdType.P_CHART,
                        lowerAmber: 2,
                        upperAmber: 3,
                        lowerRed: 4,
                        upperRed: 5,
                        plotCalculation: ModelPlotCalculation.BY_TIME_PERIOD,
                        minSubjects: 10,
                        plotBy: ModelPlotBy.WEEKS,
                        numeratorName: 'Country Numerator',
                        denominatorName: 'Country Denominator',
                    }}
                    popoutInfo={{
                        dataCutDate: '2025-27-11',
                        dataviewRef: '000000000000000000000000',
                    }}
                />
            </div>
        </div>
    );
};

export default Charts;
